/**
 * Bi-directional sync (Build → design). When the user changes the game through
 * Build, this dialog runs an AI pass that proposes edits to the design sections
 * so the spec stays true to the game, and lets the user review each one as a
 * red/green diff and accept/reject per section before anything is written.
 *
 * The reverse of "Send Build Updates": that pushes design → game; this pulls
 * game (Build) → design. See `design.ts` reconcileFromChat / applyReconcile.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import type { DesignReconcileProposal, DesignLevelCaptureItem } from '@volley/crucible-ipc-types';
import { useCrucibleClient } from '../../transport/context.js';
import {
  applyReconcile,
  reconcileFromChat,
  captureLevelsFromBuild,
  applyLevelCapture,
} from './anvil-api';
import { diffLines, type DiffRowType } from './lib/line-diff';
import {
  itemModeFor,
  mergeItemsMarkdown,
  parseItems,
  isGameLoopSection,
} from './lib/section-items';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';

const DIFF_ROW_CLASS: Record<DiffRowType, string> = {
  add: 'bg-success/12 text-success',
  del: 'bg-error/12 text-error',
  context: 'text-crucible-muted',
};
const DIFF_ROW_SIGN: Record<DiffRowType, string> = { add: '+', del: '-', context: ' ' };

export interface ReconcileMutationResult {
  applied: boolean;
  filenames: string[];
}

export interface ReconcileDialogProps {
  gameId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after sections are written or the prompt is dismissed. */
  onSettled?: (result: ReconcileMutationResult) => void;
  /** Called after the apply/dismiss request completes, even if this run is stale. */
  onMutationComplete?: (result: ReconcileMutationResult) => void;
  /** Whether a section uses the item editor (defaults to the canonical-id rule).
   *  For item sections the chat-driven update is MERGED into the existing items
   *  by name rather than replacing the body, so nothing is dropped. */
  isItemSection?: (sectionId: string) => boolean;
  /** Expert level-capture gate (CRUCIBLE_LEVEL_DESIGN_PRO). When false, the dialog reviews
   *  only chat→doc changes; build→design level capture is not scanned or offered. */
  levelCaptureEnabled?: boolean;
}

/** For an item section, merge the AI's chat-driven body into the original by
 *  name (preserving items it didn't touch) instead of replacing wholesale.
 *  Falls back to the AI body when the section isn't item-mode or the AI didn't
 *  return parseable items (so a prose update still applies). */
function reconcileBody(
  proposal: DesignReconcileProposal,
  isItemSection: (sectionId: string) => boolean,
): string {
  if (!isItemSection(proposal.sectionId)) return proposal.newBody;
  if (parseItems(proposal.newBody).items.length === 0) return proposal.newBody;
  return mergeItemsMarkdown(proposal.originalBody, proposal.newBody, {
    pinGameLoop: isGameLoopSection(proposal.sectionId),
  });
}

type Stage = 'working' | 'review' | 'error';

interface Reviewable extends DesignReconcileProposal {
  accepted: boolean;
}

export function ReconcileDialog({
  gameId,
  open,
  onOpenChange,
  onSettled,
  onMutationComplete,
  isItemSection = (id) => itemModeFor({ id }),
  levelCaptureEnabled = false,
}: ReconcileDialogProps): ReactElement {
  const client = useCrucibleClient();
  const [stage, setStage] = useState<Stage>('working');
  const [proposals, setProposals] = useState<Reviewable[]>([]);
  const [levelCaps, setLevelCaps] = useState<(DesignLevelCaptureItem & { accepted: boolean })[]>(
    [],
  );
  const [chatCount, setChatCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const runRef = useRef(0);

  useEffect(() => {
    if (!open) {
      runRef.current += 1;
      return;
    }
    const myRun = (runRef.current += 1);
    const live = (): boolean => runRef.current === myRun;
    setStage('working');
    setProposals([]);
    setLevelCaps([]);
    setError(null);
    // Both directions of build→design in one review: chat-driven doc edits AND levels the
    // build authored in the runtime that the design source hasn't captured. Levels only
    // (game-agnostic); a failed capture scan never blocks the doc reconcile.
    Promise.all([
      reconcileFromChat(client, gameId),
      levelCaptureEnabled
        ? captureLevelsFromBuild(client, gameId).catch(() => ({ items: [], noBuild: true }))
        : Promise.resolve({ items: [], noBuild: true }),
    ])
      .then(([res, caps]) => {
        if (!live()) return;
        setChatCount(res.chatCount);
        // For item sections, merge the chat-driven update into the existing
        // items by name so weapons etc. land as cards and nothing is dropped.
        setProposals(
          res.proposals.map((p) => ({
            ...p,
            newBody: reconcileBody(p, isItemSection),
            accepted: true,
          })),
        );
        setLevelCaps(caps.items.map((it) => ({ ...it, accepted: true })));
        setStage('review');
      })
      .catch((e: unknown) => {
        if (!live()) return;
        setError(e instanceof Error ? e.message : String(e));
        setStage('error');
      });
  }, [open, gameId, client, levelCaptureEnabled]);

  /**
   * Applies accepted reverse-reconcile proposals and reports written filenames.
   *
   * Args:
   *   None.
   *
   * Returns:
   *   Promise that settles after the write and parent callbacks complete.
   *
   * Raises:
   *   None. Failures are rendered in-dialog.
   */
  const apply = useCallback(async () => {
    const accepted = proposals.filter((p) => p.accepted);
    // Pass `original` so the main process can abort if the section drifted since
    // this review started (TOCTOU guard) rather than overwrite a fresh edit.
    const sections = accepted.map((p) => ({
      filename: p.filename,
      body: p.newBody,
      original: p.originalBody,
    }));
    const acceptedLevels = levelCaps.filter((l) => l.accepted).map((l) => l.level);
    const myRun = runRef.current;
    const live = (): boolean => runRef.current === myRun;
    try {
      await applyReconcile(client, gameId, sections, chatCount);
      let capturedLevels: string[] = [];
      if (acceptedLevels.length > 0) {
        capturedLevels = (await applyLevelCapture(client, gameId, acceptedLevels)).written;
      }
      const result = {
        applied: sections.length > 0 || capturedLevels.length > 0,
        filenames: [
          ...sections.map((s) => s.filename),
          ...capturedLevels.map((id) => `levels/${id}.json`),
        ],
      };
      onMutationComplete?.(result);
      if (!live()) return;
      onSettled?.(result);
      onOpenChange(false);
    } catch (e: unknown) {
      if (!live()) return;
      setError(e instanceof Error ? e.message : String(e));
      setStage('error');
    }
  }, [
    proposals,
    levelCaps,
    chatCount,
    gameId,
    onMutationComplete,
    onSettled,
    onOpenChange,
    client,
  ]);

  /**
   * Marks recent Build activity reviewed without writing design sections.
   *
   * Args:
   *   None.
   *
   * Returns:
   *   Promise that settles after the marker advancement attempt completes.
   *
   * Raises:
   *   None. Marker write failures are best-effort so the prompt can reappear.
   */
  const dismiss = useCallback(async () => {
    const myRun = runRef.current;
    const live = (): boolean => runRef.current === myRun;
    try {
      // Dismiss advances the marker only; if it fails, the same turns re-propose later.
      await applyReconcile(client, gameId, [], chatCount);
    } catch {
      // Best-effort: keep dismissal responsive even when marker persistence fails.
    }
    const result = { applied: false, filenames: [] };
    onMutationComplete?.(result);
    if (!live()) return;
    onSettled?.(result);
    onOpenChange(false);
  }, [gameId, chatCount, onMutationComplete, onSettled, onOpenChange, client]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Review Build changes</DialogTitle>
        </DialogHeader>

        {stage === 'working' && (
          <div
            className="py-6 text-center text-sm text-crucible-muted"
            data-testid="reconcile-working"
          >
            Reviewing recent Build activity...
          </div>
        )}

        {stage === 'error' && (
          <div data-testid="reconcile-error">
            <p className="mb-3 rounded border border-error/25 bg-error/12 p-2 text-xs text-error">
              {error}
            </p>
            <DialogFooter>
              <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
                Close
              </Button>
            </DialogFooter>
          </div>
        )}

        {stage === 'review' && (
          <div data-testid="reconcile-review">
            {proposals.length === 0 && levelCaps.length === 0 ? (
              <>
                <p className="text-sm font-medium text-crucible-fg" data-testid="reconcile-none">
                  No design-significant changes found.
                </p>
                <p className="mb-4 mt-1 text-sm text-crucible-muted">
                  Recent Build activity did not require updates to the Design docs.
                </p>
                <DialogFooter>
                  <Button size="sm" onClick={() => void dismiss()}>
                    Got it
                  </Button>
                </DialogFooter>
              </>
            ) : (
              <>
                {proposals.length > 0 && (
                  <p className="mb-3 text-xs text-crucible-muted">
                    {proposals.filter((p) => p.accepted).length} of {proposals.length} section
                    {proposals.length === 1 ? '' : 's'} selected to update from recent Build
                    activity.
                  </p>
                )}
                <ul className="space-y-4">
                  {proposals.map((p) => (
                    <li key={p.sectionId} className="rounded border border-crucible-border p-2">
                      <label className="flex items-center gap-2 text-sm text-crucible-fg">
                        <input
                          type="checkbox"
                          checked={p.accepted}
                          onChange={(e) =>
                            setProposals((prev) =>
                              prev.map((x) =>
                                x.sectionId === p.sectionId
                                  ? { ...x, accepted: e.target.checked }
                                  : x,
                              ),
                            )
                          }
                        />
                        <span className="font-medium">{p.title}</span>
                        {p.rationale && (
                          <span className="text-crucible-muted">— {p.rationale}</span>
                        )}
                      </label>
                      <div className="mt-2 rounded bg-crucible-bg-elevated p-2">
                        <div className="max-h-60 overflow-y-auto rounded font-mono f-accent leading-snug">
                          {diffLines(p.originalBody, p.newBody).map((row, idx) => (
                            <div
                              key={idx}
                              className={`flex gap-1.5 px-1 ${DIFF_ROW_CLASS[row.type]}`}
                            >
                              <span aria-hidden className="select-none opacity-50">
                                {DIFF_ROW_SIGN[row.type]}
                              </span>
                              <span className="min-w-0 whitespace-pre-wrap break-words">
                                {row.text || ' '}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
                {levelCaps.length > 0 && (
                  <div className="mt-4" data-testid="reconcile-levels">
                    <p className="mb-2 text-xs text-crucible-muted">
                      {levelCaps.filter((l) => l.accepted).length} of {levelCaps.length} level
                      {levelCaps.length === 1 ? '' : 's'} the Build authored/changed — capture into
                      the Design source?
                    </p>
                    <ul className="space-y-1.5">
                      {levelCaps.map((l) => (
                        <li
                          key={l.levelId}
                          className="flex items-center gap-2 rounded border border-crucible-border p-2 text-sm"
                        >
                          <input
                            type="checkbox"
                            checked={l.accepted}
                            onChange={(e) =>
                              setLevelCaps((prev) =>
                                prev.map((x) =>
                                  x.levelId === l.levelId
                                    ? { ...x, accepted: e.target.checked }
                                    : x,
                                ),
                              )
                            }
                          />
                          <span className="font-medium text-crucible-fg">{l.name}</span>
                          <span
                            className={`rounded-full border px-1.5 py-0.5 text-[10px] uppercase tracking-wide ${
                              l.status === 'new'
                                ? 'border-emerald-300/30 bg-emerald-300/10 text-emerald-300'
                                : 'border-amber-300/30 bg-amber-300/10 text-amber-200'
                            }`}
                          >
                            {l.status}
                          </span>
                          <span className="ml-auto font-mono text-[10px] text-crucible-muted">
                            {l.levelId}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <DialogFooter>
                  <Button variant="ghost" size="sm" onClick={() => void dismiss()}>
                    Dismiss
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => void apply()}
                    disabled={
                      !proposals.some((p) => p.accepted) && !levelCaps.some((l) => l.accepted)
                    }
                  >
                    Update design
                  </Button>
                </DialogFooter>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
